import React, { useEffect, useMemo, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { CheckCircle2, ClipboardList, Plus, ShieldCheck, Trash2 } from 'lucide-react';
import SiteBrandedLoader from '../components/SiteBrandedLoader';
import PersianDatePicker from '../components/PersianDatePicker';
import {
  AssessmentBackdrop,
  BRAND,
  HAIRLINE,
  Panel,
  PanelDivider,
  ProgressRing,
  StepStrip,
} from '../components/assessment/AssessmentUI';
import { recruitmentService } from '../services/api';
import type {
  RecruitmentEducationRow,
  RecruitmentCourseRow,
  RecruitmentPublicSettings,
  RecruitmentSkillRow,
  RecruitmentSubmissionPayload,
  RecruitmentWorkExperience,
} from '../types/recruitment';
import {
  MAX_LENGTHS,
  RecruitmentFieldKey,
  validateRecruitmentField,
} from '../utils/recruitmentValidation';

const DRAFT_KEY = 'recruitment-application-draft-v1';

const inputClass =
  'w-full rounded-xl border bg-white px-3 py-2.5 text-[13px] text-slate-800 outline-none transition focus:border-[#9DB0E4]';
const inputErrorClass = 'border-red-300 focus:border-red-400';

const emptyForm = (): RecruitmentSubmissionPayload => ({
  fullName: '',
  lastName: '',
  mobile: '',
  workConditions: {},
  education: [{}, {}, {}],
  skills: [{}, {}, {}],
  courses: [{}],
  workExperiences: [{}, {}, {}],
  narrative: {},
});

// ─── اجزای ورودی عمومی ────────────────────────────────────────────────────────

const Field: React.FC<{
  label: string;
  value: string;
  onChange: (v: string) => void;
  onBlur?: () => void;
  error?: string;
  long?: boolean;
  required?: boolean;
  placeholder?: string;
  maxLength?: number;
  span2?: boolean;
  dir?: 'ltr' | 'rtl';
  inputMode?: 'text' | 'numeric' | 'tel' | 'email';
  type?: 'text' | 'email' | 'tel';
}> = ({ label, value, onChange, onBlur, error, long, required, placeholder, maxLength, span2, dir, inputMode, type }) => (
  <label className={span2 ? 'sm:col-span-2' : ''}>
    <span className="mb-1 block text-[11.5px] font-black text-slate-600">
      {label}
      {required ? <span className="text-red-500"> *</span> : null}
    </span>
    {long ? (
      <textarea
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onBlur={onBlur}
        placeholder={placeholder}
        maxLength={maxLength}
        rows={4}
        className={`${inputClass} ${error ? inputErrorClass : ''}`}
        style={{ borderColor: error ? undefined : HAIRLINE }}
      />
    ) : (
      <input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onBlur={onBlur}
        placeholder={placeholder}
        maxLength={maxLength}
        dir={dir}
        inputMode={inputMode}
        type={type || 'text'}
        className={`${inputClass} ${error ? inputErrorClass : ''}`}
        style={{ borderColor: error ? undefined : HAIRLINE }}
      />
    )}
    {error ? <p className="mt-1 text-[11px] font-bold text-red-500">{error}</p> : null}
  </label>
);

const ChoiceGroup: React.FC<{
  label?: string;
  options: string[];
  value: string;
  onChange: (v: string) => void;
  multiline?: boolean;
}> = ({ label, options, value, onChange }) => (
  <div>
    {label ? <span className="mb-2 block text-[11.5px] font-black text-slate-600">{label}</span> : null}
    <div className="flex flex-wrap gap-2">
      {options.map((opt) => {
        const active = value === opt;
        return (
          <button
            key={opt}
            type="button"
            onClick={() => onChange(opt)}
            className="rounded-full border px-3 py-1.5 text-[12px] font-bold transition"
            style={{
              borderColor: active ? BRAND : HAIRLINE,
              backgroundColor: active ? `${BRAND}12` : 'white',
              color: active ? BRAND : '#475569',
            }}
          >
            {opt}
          </button>
        );
      })}
    </div>
  </div>
);

const CheckboxGroup: React.FC<{
  label?: string;
  options: string[];
  values: string[];
  onChange: (v: string[]) => void;
}> = ({ label, options, values, onChange }) => (
  <div>
    {label ? <span className="mb-2 block text-[11.5px] font-black text-slate-600">{label}</span> : null}
    <div className="flex flex-wrap gap-2">
      {options.map((opt) => {
        const active = values.includes(opt);
        return (
          <button
            key={opt}
            type="button"
            onClick={() =>
              onChange(active ? values.filter((v) => v !== opt) : [...values, opt])
            }
            className="rounded-full border px-3 py-1.5 text-[12px] font-bold transition"
            style={{
              borderColor: active ? BRAND : HAIRLINE,
              backgroundColor: active ? `${BRAND}12` : 'white',
              color: active ? BRAND : '#475569',
            }}
          >
            {opt}
          </button>
        );
      })}
    </div>
  </div>
);

/** جدول ردیف‌های تکرارشونده — تحصیلات، مهارت‌ها، دوره‌ها */
function RepeatableRows<T extends Record<string, any>>({
  rows,
  onChange,
  columns,
  addLabel,
  maxRows,
}: {
  rows: T[];
  onChange: (rows: T[]) => void;
  columns: Array<{ key: keyof T; label: string; placeholder?: string; maxLength?: number }>;
  addLabel: string;
  maxRows: number;
}) {
  return (
    <div className="space-y-3">
      {rows.map((row, index) => (
        <div
          key={index}
          className="grid grid-cols-1 gap-2 rounded-2xl border p-3 sm:grid-cols-2 lg:grid-cols-5"
          style={{ borderColor: HAIRLINE }}
        >
          {columns.map((col) => (
            <label key={String(col.key)}>
              <span className="mb-1 block text-[10.5px] font-black text-slate-500">
                {col.label}
              </span>
              <input
                value={row[col.key] || ''}
                placeholder={col.placeholder}
                maxLength={col.maxLength}
                onChange={(e) => {
                  const next = rows.slice();
                  next[index] = { ...next[index], [col.key]: e.target.value };
                  onChange(next);
                }}
                className={`${inputClass} py-2 text-[12px]`}
                style={{ borderColor: HAIRLINE }}
              />
            </label>
          ))}
          {rows.length > 1 ? (
            <button
              type="button"
              onClick={() => onChange(rows.filter((_, i) => i !== index))}
              className="flex items-center justify-center gap-1 self-end rounded-xl border px-3 py-2 text-[11px] font-bold text-red-500"
              style={{ borderColor: '#FBD5D5' }}
            >
              <Trash2 size={14} /> حذف ردیف
            </button>
          ) : null}
        </div>
      ))}
      {rows.length < maxRows ? (
        <button
          type="button"
          onClick={() => onChange([...rows, {} as T])}
          className="flex items-center gap-1.5 rounded-xl border px-3 py-2 text-[12px] font-bold"
          style={{ borderColor: HAIRLINE, color: BRAND }}
        >
          <Plus size={14} /> {addLabel}
        </button>
      ) : null}
    </div>
  );
}

// ─── تنظیمات ثابت گزینه‌ها ────────────────────────────────────────────────────

const CURRENT_STATUS_OPTIONS = ['شاغل تمام‌وقت', 'شاغل پاره‌وقت', 'فریلنس / پروژه‌ای', 'در حال حاضر شاغل نیستم'];
const AVAILABLE_FROM_OPTIONS = ['بلافاصله', 'تا ۲ هفته', '۲ تا ۴ هفته', 'بیشتر از یک ماه'];
const COOPERATION_TYPE_OPTIONS = ['تمام‌وقت', 'پاره‌وقت', 'پروژه‌ای', 'کارآموزی', 'قابل مذاکره'];
const MARITAL_OPTIONS = ['مجرد', 'متأهل', 'سایر / ترجیح می‌دهم اعلام نکنم'];
const MILITARY_OPTIONS = ['پایان خدمت', 'معافیت دائم', 'معافیت تحصیلی', 'معافیت موقت', 'در حال خدمت', 'مشمول', 'غیرمشمول'];
const TIME_MGMT_OPTIONS = ['تقویم', 'نرم‌افزار مدیریت کار', 'چک‌لیست', 'دفتر / یادداشت', 'یادآور موبایل', 'روش شخصی', 'سایر'];
const FIRST_REACTION_OPTIONS = [
  'اطلاعات جمع می‌کنم',
  'سریع یک اقدام کوچک انجام می‌دهم',
  'با فرد باتجربه‌تر مشورت می‌کنم',
  'مسئله را به بخش‌های کوچک تقسیم می‌کنم',
  'اول مهم‌ترین ریسک را پیدا می‌کنم',
  'بستگی به شرایط دارد',
];
const ANNOYANCE_OPTIONS = ['بی‌نظمی', 'بی‌مسئولیتی', 'کندی', 'بی‌احترامی', 'مقاومت در برابر تغییر', 'کیفیت پایین', 'جلسات بی‌نتیجه'];
const HEARD_FROM_OPTIONS = [
  'اینستاگرام / شبکه‌های اجتماعی',
  'وب‌سایت',
  'معرفی دوستان / آشنایان',
  'یکی از کارکنان مجموعه',
  'دوره‌ها / محصولات مجموعه',
  'آگهی استخدام',
  'جست‌وجوی اینترنتی',
  'سایر',
];

type Mode = 'loading' | 'intro' | 'wizard' | 'success';

const STEP_TITLES = [
  'اطلاعات فردی و تماس',
  'شرایط همکاری',
  'تحصیلات و وضعیت خدمت',
  'مهارت‌های تخصصی',
  'دوره‌ها و کارگاه‌ها',
  'سوابق و تجارب شغلی',
  'دستاورد و اثرگذاری',
  'بحران، فشار و تصمیم‌گیری',
  'چالش و حل مسئله',
  'نوآوری و بهبود',
  'مسئولیت‌پذیری',
  'نظم و مدیریت کار',
  'فرهنگ سازمانی',
  'چند انتخاب کوتاه',
  'درباره همکاری با ما',
];

const Shell: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <AssessmentBackdrop>
    <div className="mx-auto max-w-3xl px-3 pb-16 pt-6 sm:px-6">{children}</div>
  </AssessmentBackdrop>
);

const RecruitmentApplication: React.FC = () => {
  const [mode, setMode] = useState<Mode>('loading');
  const [settings, setSettings] = useState<RecruitmentPublicSettings | null>(null);
  const [stepIndex, setStepIndex] = useState(0);
  const [form, setForm] = useState<RecruitmentSubmissionPayload>(emptyForm());
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [errors, setErrors] = useState<Partial<Record<RecruitmentFieldKey, string>>>({});

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const s = await recruitmentService.getSettings();
        if (mounted) setSettings(s);
      } catch {
        // متن معرفی صرفاً تزئینی است؛ نبودش نباید مانع فرم شود
      }
      try {
        const raw = localStorage.getItem(DRAFT_KEY);
        if (raw && mounted) setForm({ ...emptyForm(), ...JSON.parse(raw) });
      } catch {
        // پیش‌نویس خراب را نادیده بگیر
      }
      if (mounted) setMode('intro');
    })();
    return () => {
      mounted = false;
    };
  }, []);

  useEffect(() => {
    if (mode !== 'wizard') return;
    const timeout = setTimeout(() => {
      try {
        localStorage.setItem(DRAFT_KEY, JSON.stringify(form));
      } catch {
        // localStorage پر یا غیرفعال است؛ از دست دادن پیش‌نویس فقط ناراحت‌کننده است، بحرانی نیست
      }
    }, 600);
    return () => clearTimeout(timeout);
  }, [form, mode]);

  const narrative = form.narrative || {};
  const setNarrative = (patch: Partial<typeof narrative>) =>
    setForm((f) => ({ ...f, narrative: { ...f.narrative, ...patch } }));
  const setWorkConditions = (patch: Partial<NonNullable<typeof form.workConditions>>) =>
    setForm((f) => ({ ...f, workConditions: { ...f.workConditions, ...patch } }));

  const runFieldValidation = (key: RecruitmentFieldKey, value: string) => {
    const message = validateRecruitmentField(key, value);
    setErrors((prev) => {
      const next = { ...prev };
      if (message) next[key] = message;
      else delete next[key];
      return next;
    });
    return !message;
  };

  /** اعتبارسنجی کل بخش ۱ (اطلاعات فردی) — هم برای جلوگیری از رد شدن، هم نمایش خطاها */
  const validateStepOne = (): boolean => {
    const checks: Array<[RecruitmentFieldKey, string]> = [
      ['fullName', form.fullName],
      ['lastName', form.lastName],
      ['mobile', form.mobile],
      ['email', form.email || ''],
      ['nationalId', form.nationalId || ''],
      ['phone', form.phone || ''],
    ];
    const results = checks.map(([key, value]) => runFieldValidation(key, value));
    return results.every(Boolean);
  };

  const isStepOneValid = useMemo(() => {
    const noError = (key: RecruitmentFieldKey, value: string) => !validateRecruitmentField(key, value);
    return (
      form.fullName.trim().length >= 2 &&
      form.lastName.trim().length >= 2 &&
      noError('mobile', form.mobile) &&
      noError('email', form.email || '') &&
      noError('nationalId', form.nationalId || '') &&
      noError('phone', form.phone || '')
    );
  }, [form.fullName, form.lastName, form.mobile, form.email, form.nationalId, form.phone]);

  const stepComplete = useMemo(
    () => STEP_TITLES.map((_, index) => (index === 0 ? isStepOneValid : true)),
    [isStepOneValid],
  );

  const goNext = () => {
    if (stepIndex === 0 && !validateStepOne()) return;
    setStepIndex((i) => Math.min(STEP_TITLES.length - 1, i + 1));
  };
  const goPrev = () => setStepIndex((i) => Math.max(0, i - 1));

  const jumpToStep = (index: number) => {
    if (index > stepIndex && stepIndex === 0 && !validateStepOne()) return;
    setStepIndex(index);
  };

  const handleSubmit = async () => {
    if (!validateStepOne()) {
      setStepIndex(0);
      setSubmitError('لطفاً خطاهای بخش «اطلاعات فردی و تماس» را برطرف کنید.');
      return;
    }
    setSubmitting(true);
    setSubmitError(null);
    try {
      await recruitmentService.submit(form);
      localStorage.removeItem(DRAFT_KEY);
      setMode('success');
    } catch (err: any) {
      setSubmitError(
        err?.response?.data?.message || 'ثبت فرم با خطا مواجه شد. لطفاً دوباره تلاش کنید.',
      );
    } finally {
      setSubmitting(false);
    }
  };

  if (mode === 'loading') {
    return (
      <Shell>
        <SiteBrandedLoader />
      </Shell>
    );
  }

  if (mode === 'intro') {
    return (
      <Shell>
        <Panel className="p-5 sm:p-8">
          <div className="flex items-center gap-3">
            <span
              className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl"
              style={{ backgroundColor: `${BRAND}14`, color: BRAND }}
            >
              <ClipboardList size={22} />
            </span>
            <div>
              <h1 className="text-[16px] font-black text-slate-900 sm:text-[17px]">فرم جامع درخواست همکاری و استخدام</h1>
              <p className="text-[11px] font-bold text-slate-400 sm:text-[11.5px]">ویژه مجموعه‌های آموزشی و توسعه فردی</p>
            </div>
          </div>
          <PanelDivider className="my-5" />
          <p className="text-[13px] leading-7 text-slate-600">
            {settings?.introText ||
              'برای ما فقط رزومه مهم نیست؛ نحوه فکر کردن، مسئولیت‌پذیری، نظم، عملکرد در چالش و بحران، نتیجه‌گرایی، نوآوری و احترام به فرهنگ سازمانی نیز اهمیت دارد.'}
          </p>
          {settings?.privacyNote ? (
            <div
              className="mt-4 flex items-start gap-2 rounded-2xl border p-3 text-[11.5px] font-bold text-slate-500"
              style={{ borderColor: HAIRLINE }}
            >
              <ShieldCheck size={16} className="mt-0.5 shrink-0" style={{ color: BRAND }} />
              {settings.privacyNote}
            </div>
          ) : null}
          <button
            type="button"
            onClick={() => setMode('wizard')}
            className="mt-6 w-full rounded-2xl px-4 py-3 text-[13.5px] font-black text-white transition"
            style={{ backgroundColor: BRAND }}
          >
            شروع تکمیل فرم
          </button>
        </Panel>
      </Shell>
    );
  }

  if (mode === 'success') {
    return (
      <Shell>
        <Panel className="flex flex-col items-center gap-3 p-8 text-center">
          <CheckCircle2 size={40} style={{ color: '#2E8B6B' }} />
          <h1 className="text-[16px] font-black text-slate-900">فرم شما با موفقیت ثبت شد</h1>
          <p className="text-[12.5px] font-bold text-slate-500">
            از وقتی که گذاشتید سپاسگزاریم. در صورت هماهنگی، تیم ما با شما تماس خواهد گرفت.
          </p>
        </Panel>
      </Shell>
    );
  }

  return (
    <Shell>
      <div className="mb-4 flex items-center gap-3">
        <ProgressRing value={((stepIndex + 1) / STEP_TITLES.length) * 100} />
        <div className="min-w-0 flex-1">
          <p className="text-[11px] font-black text-slate-400">
            بخش {(stepIndex + 1).toLocaleString('fa-IR')} از {STEP_TITLES.length.toLocaleString('fa-IR')}
          </p>
          <p className="truncate text-[13.5px] font-black text-slate-900">{STEP_TITLES[stepIndex]}</p>
        </div>
      </div>
      <StepStrip
        sections={STEP_TITLES.map((t, i) => ({ id: String(i), title: t }))}
        current={stepIndex}
        completed={stepComplete}
        onJump={jumpToStep}
      />

      <AnimatePresence mode="wait">
        <motion.div
          key={stepIndex}
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -8 }}
          transition={{ duration: 0.18 }}
        >
          <Panel className="mt-4 p-4 sm:p-6">
            {stepIndex === 0 && (
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <Field
                  label="نام"
                  required
                  value={form.fullName}
                  onChange={(v) => setForm((f) => ({ ...f, fullName: v }))}
                  onBlur={() => runFieldValidation('fullName', form.fullName)}
                  error={errors.fullName}
                  maxLength={MAX_LENGTHS.fullName}
                />
                <Field
                  label="نام خانوادگی"
                  required
                  value={form.lastName}
                  onChange={(v) => setForm((f) => ({ ...f, lastName: v }))}
                  onBlur={() => runFieldValidation('lastName', form.lastName)}
                  error={errors.lastName}
                  maxLength={MAX_LENGTHS.lastName}
                />
                <Field
                  label="نام پدر"
                  value={form.fatherName || ''}
                  onChange={(v) => setForm((f) => ({ ...f, fatherName: v }))}
                  maxLength={MAX_LENGTHS.fatherName}
                />
                <label>
                  <span className="mb-1 block text-[11.5px] font-black text-slate-600">تاریخ تولد</span>
                  <PersianDatePicker
                    value={form.birthDate || ''}
                    onChange={(v) => setForm((f) => ({ ...f, birthDate: v }))}
                    placeholder="مثلاً ۱۳۷۵/۰۴/۱۲"
                  />
                </label>
                <Field
                  label="محل تولد"
                  value={form.birthPlace || ''}
                  onChange={(v) => setForm((f) => ({ ...f, birthPlace: v }))}
                  maxLength={MAX_LENGTHS.birthPlace}
                />
                <Field
                  label="ملیت"
                  value={form.nationality || ''}
                  onChange={(v) => setForm((f) => ({ ...f, nationality: v }))}
                  maxLength={MAX_LENGTHS.nationality}
                />
                <Field
                  label="کد ملی"
                  value={form.nationalId || ''}
                  onChange={(v) => setForm((f) => ({ ...f, nationalId: v.replace(/[^\d]/g, '') }))}
                  onBlur={() => runFieldValidation('nationalId', form.nationalId || '')}
                  error={errors.nationalId}
                  maxLength={10}
                  dir="ltr"
                  inputMode="numeric"
                />
                <Field
                  label="شماره موبایل"
                  required
                  value={form.mobile}
                  onChange={(v) => setForm((f) => ({ ...f, mobile: v.replace(/[^\d+]/g, '') }))}
                  onBlur={() => runFieldValidation('mobile', form.mobile)}
                  error={errors.mobile}
                  placeholder="09xxxxxxxxx"
                  maxLength={13}
                  dir="ltr"
                  inputMode="tel"
                  type="tel"
                />
                <Field
                  label="شماره تلفن ثابت"
                  value={form.phone || ''}
                  onChange={(v) => setForm((f) => ({ ...f, phone: v.replace(/[^\d]/g, '') }))}
                  onBlur={() => runFieldValidation('phone', form.phone || '')}
                  error={errors.phone}
                  maxLength={MAX_LENGTHS.phone}
                  dir="ltr"
                  inputMode="tel"
                  type="tel"
                />
                <Field
                  label="ایمیل"
                  value={form.email || ''}
                  onChange={(v) => setForm((f) => ({ ...f, email: v }))}
                  onBlur={() => runFieldValidation('email', form.email || '')}
                  error={errors.email}
                  dir="ltr"
                  inputMode="email"
                  type="email"
                />
                <Field
                  label="عنوان موقعیت شغلی"
                  value={form.positionTitle || ''}
                  onChange={(v) => setForm((f) => ({ ...f, positionTitle: v }))}
                  maxLength={MAX_LENGTHS.positionTitle}
                  span2
                />
                <Field
                  label="نشانی محل سکونت"
                  value={form.address || ''}
                  onChange={(v) => setForm((f) => ({ ...f, address: v }))}
                  maxLength={MAX_LENGTHS.address}
                  long
                  span2
                />
                <div className="sm:col-span-2">
                  <ChoiceGroup label="وضعیت تأهل" options={MARITAL_OPTIONS} value={form.maritalStatus || ''} onChange={(v) => setForm((f) => ({ ...f, maritalStatus: v }))} />
                </div>
              </div>
            )}

            {stepIndex === 1 && (
              <div className="space-y-4">
                <ChoiceGroup label="وضعیت فعلی" options={CURRENT_STATUS_OPTIONS} value={form.workConditions?.currentStatus || ''} onChange={(v) => setWorkConditions({ currentStatus: v })} />
                <ChoiceGroup label="زمان امکان شروع همکاری" options={AVAILABLE_FROM_OPTIONS} value={form.workConditions?.availableFrom || ''} onChange={(v) => setWorkConditions({ availableFrom: v })} />
                <ChoiceGroup label="نوع همکاری موردنظر" options={COOPERATION_TYPE_OPTIONS} value={form.workConditions?.cooperationType || ''} onChange={(v) => setWorkConditions({ cooperationType: v })} />
                <Field label="حقوق / دستمزد مورد انتظار" value={form.workConditions?.expectedSalary || ''} onChange={(v) => setWorkConditions({ expectedSalary: v })} maxLength={MAX_LENGTHS.expectedSalary} />
                <Field label="محدودیت روز یا ساعت کاری" value={form.workConditions?.hoursLimitation || ''} onChange={(v) => setWorkConditions({ hoursLimitation: v })} maxLength={MAX_LENGTHS.hoursLimitation} />
              </div>
            )}

            {stepIndex === 2 && (
              <div className="space-y-5">
                <RepeatableRows<RecruitmentEducationRow>
                  rows={form.education || []}
                  onChange={(rows) => setForm((f) => ({ ...f, education: rows }))}
                  addLabel="افزودن مقطع تحصیلی"
                  maxRows={5}
                  columns={[
                    { key: 'degree', label: 'مقطع', maxLength: MAX_LENGTHS.educationDegree },
                    { key: 'field', label: 'رشته', maxLength: MAX_LENGTHS.educationField },
                    { key: 'university', label: 'دانشگاه / مؤسسه', maxLength: MAX_LENGTHS.educationUniversity },
                    { key: 'endYear', label: 'سال پایان', maxLength: MAX_LENGTHS.educationEndYear },
                    { key: 'status', label: 'وضعیت', maxLength: MAX_LENGTHS.educationStatus },
                  ]}
                />
                <ChoiceGroup label="وضعیت خدمت نظام وظیفه (در صورت شمول)" options={MILITARY_OPTIONS} value={form.militaryServiceStatus || ''} onChange={(v) => setForm((f) => ({ ...f, militaryServiceStatus: v }))} />
              </div>
            )}

            {stepIndex === 3 && (
              <div className="space-y-5">
                <RepeatableRows<RecruitmentSkillRow>
                  rows={form.skills || []}
                  onChange={(rows) => setForm((f) => ({ ...f, skills: rows }))}
                  addLabel="افزودن مهارت"
                  maxRows={10}
                  columns={[
                    { key: 'tool', label: 'نرم‌افزار / ابزار تخصصی', maxLength: MAX_LENGTHS.skillTool },
                    { key: 'level', label: 'سطح تسلط', maxLength: MAX_LENGTHS.skillLevel },
                    { key: 'experienceDuration', label: 'مدت تجربه', maxLength: MAX_LENGTHS.skillExperienceDuration },
                    { key: 'lastUsed', label: 'آخرین استفاده', maxLength: MAX_LENGTHS.skillLastUsed },
                  ]}
                />
                <Field label="سایر مهارت‌های تخصصی" long value={form.otherSkills || ''} onChange={(v) => setForm((f) => ({ ...f, otherSkills: v }))} maxLength={MAX_LENGTHS.otherSkills} />
              </div>
            )}

            {stepIndex === 4 && (
              <RepeatableRows<RecruitmentCourseRow>
                rows={form.courses || []}
                onChange={(rows) => setForm((f) => ({ ...f, courses: rows }))}
                addLabel="افزودن دوره"
                maxRows={10}
                columns={[
                  { key: 'title', label: 'عنوان دوره / سمینار', maxLength: MAX_LENGTHS.courseTitle },
                  { key: 'organizer', label: 'برگزارکننده', maxLength: MAX_LENGTHS.courseOrganizer },
                  { key: 'year', label: 'سال', maxLength: MAX_LENGTHS.courseYear },
                  { key: 'duration', label: 'مدت', maxLength: MAX_LENGTHS.courseDuration },
                  { key: 'usage', label: 'کاربرد / یادگیری اصلی', maxLength: MAX_LENGTHS.courseUsage },
                ]}
              />
            )}

            {stepIndex === 5 && (
              <div className="space-y-5">
                <p className="text-[11.5px] font-bold text-slate-400">
                  برای هر تجربه، علاوه بر وظایف، نتیجه و اثر واقعی خودتان را بنویسید.
                </p>
                {(form.workExperiences || []).map((exp, index) => {
                  const update = (patch: Partial<RecruitmentWorkExperience>) => {
                    const next = (form.workExperiences || []).slice();
                    next[index] = { ...next[index], ...patch };
                    setForm((f) => ({ ...f, workExperiences: next }));
                  };
                  return (
                    <div key={index} className="rounded-2xl border p-3 sm:p-4" style={{ borderColor: HAIRLINE }}>
                      <p className="mb-3 text-[12.5px] font-black text-slate-700">
                        تجربه شغلی {(index + 1).toLocaleString('fa-IR')}
                      </p>
                      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                        <Field label="نام شرکت / مجموعه" value={exp.companyName || ''} onChange={(v) => update({ companyName: v })} maxLength={MAX_LENGTHS.experienceCompanyName} />
                        <Field label="زمینه فعالیت" value={exp.field || ''} onChange={(v) => update({ field: v })} maxLength={MAX_LENGTHS.experienceField} />
                        <Field label="سمت" value={exp.position || ''} onChange={(v) => update({ position: v })} maxLength={MAX_LENGTHS.experiencePosition} />
                        <Field label="مدیر مستقیم / سمت وی" value={exp.managerNameOrTitle || ''} onChange={(v) => update({ managerNameOrTitle: v })} maxLength={MAX_LENGTHS.experienceManagerNameOrTitle} />
                        <label>
                          <span className="mb-1 block text-[11.5px] font-black text-slate-600">از (تاریخ)</span>
                          <PersianDatePicker value={exp.durationFrom || ''} onChange={(v) => update({ durationFrom: v })} />
                        </label>
                        <label>
                          <span className="mb-1 block text-[11.5px] font-black text-slate-600">تا (تاریخ)</span>
                          <PersianDatePicker value={exp.durationTo || ''} onChange={(v) => update({ durationTo: v })} placeholder="خالی = تا اکنون" />
                        </label>
                        <Field span2 long label="مهم‌ترین مسئولیت‌ها" value={exp.mainResponsibilities || ''} onChange={(v) => update({ mainResponsibilities: v })} maxLength={MAX_LENGTHS.experienceMainResponsibilities} />
                        <Field span2 long label="دلیل پایان همکاری" value={exp.endReason || ''} onChange={(v) => update({ endReason: v })} maxLength={MAX_LENGTHS.experienceEndReason} />
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {stepIndex === 6 && (
              <div className="space-y-4">
                <Field long label="از بین تمام تجربه‌های کاری‌تان، به کدام دستاورد بیشتر افتخار می‌کنید؟ (قبل از اقدام شما شرایط چگونه بود؟ دقیقاً چه کردید؟ نتیجه چه شد؟)" value={narrative.proudestAchievement || ''} onChange={(v) => setNarrative({ proudestAchievement: v })} maxLength={MAX_LENGTHS.narrativeLong} />
                <Field long label="چه چیزی در یکی از محل‌های کار قبلی بعد از حضور شما واقعاً بهتر شد؟" value={narrative.improvedSomethingAfterJoining || ''} onChange={(v) => setNarrative({ improvedSomethingAfterJoining: v })} maxLength={MAX_LENGTHS.narrativeLong} />
                <Field long label="بهترین کاری که انجام دادید بدون اینکه کسی مستقیماً از شما خواسته باشد چه بود؟ چرا خودتان وارد عمل شدید؟" value={narrative.bestUnaskedInitiative || ''} onChange={(v) => setNarrative({ bestUnaskedInitiative: v })} maxLength={MAX_LENGTHS.narrativeLong} />
                <Field long label="اگر با مدیر قبلی شما تماس بگیریم و بپرسیم «مهم‌ترین ارزشی که حضور این فرد ایجاد کرد چه بود؟» فکر می‌کنید چه می‌گوید؟" value={narrative.managerReferenceGuess || ''} onChange={(v) => setNarrative({ managerReferenceGuess: v })} maxLength={MAX_LENGTHS.narrativeLong} />
              </div>
            )}

            {stepIndex === 7 && (
              <div className="space-y-4">
                <Field long label="ساعت ۱۰ صبح است: یک مشتری ناراضی منتظر پاسخ است، مدیر یک درخواست فوری دارد، پروژه‌ای امروز موعد تحویل دارد و همکارتان هم به کمک نیاز دارد. از کجا شروع می‌کنید و چرا؟" value={narrative.multiPressureScenario || ''} onChange={(v) => setNarrative({ multiPressureScenario: v })} maxLength={MAX_LENGTHS.narrativeLong} />
                <Field long label="چند ساعت قبل از موعد تحویل متوجه می‌شوید احتمالاً کار به زمان توافق‌شده نمی‌رسد. چه می‌کنید و چه زمانی مدیر یا تیم را مطلع می‌کنید؟" value={narrative.lateDeliveryScenario || ''} onChange={(v) => setNarrative({ lateDeliveryScenario: v })} maxLength={MAX_LENGTHS.narrativeLong} />
                <Field long label="سخت‌ترین بحران یا موقعیت پرفشاری که در محیط کار تجربه کرده‌اید چه بوده؟ (شما دقیقاً چه کردید و نتیجه چه شد)" value={narrative.hardestCrisisExperienced || ''} onChange={(v) => setNarrative({ hardestCrisisExperienced: v })} maxLength={MAX_LENGTHS.narrativeLong} />
                <Field long label="کمتر از ۲۴ ساعت تا برگزاری یک دوره مهم باقی مانده؛ یکی از افراد کلیدی غایب است، بخشی از کار آماده نیست و تعدادی از شرکت‌کنندگان ناراضی‌اند. اولین سه اقدام شما چیست؟" value={narrative.last24hCrisisScenario || ''} onChange={(v) => setNarrative({ last24hCrisisScenario: v })} maxLength={MAX_LENGTHS.narrativeLong} />
              </div>
            )}

            {stepIndex === 8 && (
              <div className="space-y-4">
                <Field long label="کاری به شما سپرده شده که قبلاً انجام نداده‌اید و دستورالعمل کاملی هم وجود ندارد. از کجا شروع می‌کنید؟" value={narrative.noInstructionTask || ''} onChange={(v) => setNarrative({ noInstructionTask: v })} maxLength={MAX_LENGTHS.narrativeLong} />
                <Field long label="اگر چند بار برای حل یک مسئله تلاش کنید و نتیجه نگیرید، از کجا می‌فهمید باید بیشتر تلاش کنید، روش را عوض کنید یا کمک بگیرید؟" value={narrative.repeatedFailureApproach || ''} onChange={(v) => setNarrative({ repeatedFailureApproach: v })} maxLength={MAX_LENGTHS.narrativeLong} />
              </div>
            )}

            {stepIndex === 9 && (
              <div className="space-y-4">
                <Field long label="آخرین باری که با خودتان گفتید «این کار را می‌شود بهتر انجام داد» چه زمانی بود؟ چه چیزی را تغییر دادید و نتیجه چه شد؟" value={narrative.lastImprovementIdea || ''} onChange={(v) => setNarrative({ lastImprovementIdea: v })} maxLength={MAX_LENGTHS.narrativeLong} />
                <Field long label="در مجموعه‌ای متوجه می‌شوید یک فرایند ناکارآمد است و می‌شنوید «اینجا همیشه همین‌طوری انجام شده». چه می‌کنید؟" value={narrative.inefficientProcessReaction || ''} onChange={(v) => setNarrative({ inefficientProcessReaction: v })} maxLength={MAX_LENGTHS.narrativeLong} />
                <Field long label="ایده‌ای دارید که فکر می‌کنید عملکرد مجموعه را بهتر می‌کند، اما مدیرتان قانع نشده است. چه کار می‌کنید؟" value={narrative.unconvincedManagerIdea || ''} onChange={(v) => setNarrative({ unconvincedManagerIdea: v })} maxLength={MAX_LENGTHS.narrativeLong} />
                <ChoiceGroup
                  label="کدام جمله به رفتار واقعی شما نزدیک‌تر است؟"
                  options={['A) بی‌دلیل تغییرش نمی‌دهم', 'B) کنجکاوم ببینم بهتر هم می‌شود یا نه', 'C) اول فایده، هزینه و ریسک را می‌سنجم']}
                  value={
                    narrative.behaviorChoice === 'A'
                      ? 'A) بی‌دلیل تغییرش نمی‌دهم'
                      : narrative.behaviorChoice === 'B'
                        ? 'B) کنجکاوم ببینم بهتر هم می‌شود یا نه'
                        : narrative.behaviorChoice === 'C'
                          ? 'C) اول فایده، هزینه و ریسک را می‌سنجم'
                          : ''
                  }
                  onChange={(v) => setNarrative({ behaviorChoice: v.startsWith('A') ? 'A' : v.startsWith('B') ? 'B' : 'C' })}
                />
                <Field long label="چرا این گزینه را انتخاب کردید؟" value={narrative.behaviorChoiceReason || ''} onChange={(v) => setNarrative({ behaviorChoiceReason: v })} maxLength={MAX_LENGTHS.narrativeShort1000} />
                <Field long label="اگر ایده جدیدی اجرا شود و نتیجه ندهد، قدم بعدی شما چیست؟" value={narrative.failedIdeaNextStep || ''} onChange={(v) => setNarrative({ failedIdeaNextStep: v })} maxLength={MAX_LENGTHS.narrativeLong} />
              </div>
            )}

            {stepIndex === 10 && (
              <div className="space-y-4">
                <Field long label="آخرین باری که قول انجام کاری را دادید اما نتوانستید طبق قولتان عمل کنید چه زمانی بود؟ چه شد و بعد از آن چه کردید؟" value={narrative.brokenPromiseExperience || ''} onChange={(v) => setNarrative({ brokenPromiseExperience: v })} maxLength={MAX_LENGTHS.narrativeLong} />
                <Field long label="یکی از اشتباهات کاری خودتان را تعریف کنید که باعث ایجاد مشکل شد. سهم خودتان چه بود، چطور مدیریت کردید و بعد از آن چه چیزی را تغییر دادید؟" value={narrative.ownMistakeExample || ''} onChange={(v) => setNarrative({ ownMistakeExample: v })} maxLength={MAX_LENGTHS.narrativeLong} />
                <Field long label="وقتی نتیجه یک کار خوب نمی‌شود، معمولاً اولین چیزی که بررسی می‌کنید چیست؟ با یک مثال واقعی توضیح دهید." value={narrative.firstCheckWhenResultIsBad || ''} onChange={(v) => setNarrative({ firstCheckWhenResultIsBad: v })} maxLength={MAX_LENGTHS.narrativeLong} />
              </div>
            )}

            {stepIndex === 11 && (
              <div className="space-y-4">
                <Field long label="فرض کنید در یک هفته ۷ کار مهم دارید. چطور مطمئن می‌شوید هیچ‌کدام فراموش یا دیر انجام نمی‌شود؟" value={narrative.sevenTasksInAWeek || ''} onChange={(v) => setNarrative({ sevenTasksInAWeek: v })} maxLength={MAX_LENGTHS.narrativeLong} />
                <CheckboxGroup label="برای مدیریت کار و زمان بیشتر از چه روشی استفاده می‌کنید؟" options={TIME_MGMT_OPTIONS} values={narrative.timeManagementMethods || []} onChange={(v) => setNarrative({ timeManagementMethods: v })} />
                <Field long label="اگر انجام کاری سه روز زمان ببرد و موعد تحویل آن یک هفته دیگر باشد، معمولاً چه زمانی شروع می‌کنید و چرا؟" value={narrative.startTimingReasoning || ''} onChange={(v) => setNarrative({ startTimingReasoning: v })} maxLength={MAX_LENGTHS.narrativeLong} />
              </div>
            )}

            {stepIndex === 12 && (
              <div className="space-y-4">
                <Field long label="با تصمیم تیم مخالف هستید؛ نظرتان را مطرح کرده‌اید اما تصمیم نهایی تغییر نکرده است. از این لحظه به بعد چه می‌کنید؟" value={narrative.disagreeWithTeamDecision || ''} onChange={(v) => setNarrative({ disagreeWithTeamDecision: v })} maxLength={MAX_LENGTHS.narrativeLong} />
                <Field long label="از نظر شما مرز بین «داشتن نظر مستقل» و «بی‌احترامی به تصمیم تیم» کجاست؟" value={narrative.independentVsDisrespectfulLine || ''} onChange={(v) => setNarrative({ independentVsDisrespectfulLine: v })} maxLength={MAX_LENGTHS.narrativeMedium2000} />
                <Field long label="همکاری در جمع مرتب یکی از تصمیم‌های مدیریت را مسخره می‌کند و شما هم با بخشی از نقد او موافقید. چه واکنشی نشان می‌دهید؟" value={narrative.mockingManagementDecisionReaction || ''} onChange={(v) => setNarrative({ mockingManagementDecisionReaction: v })} maxLength={MAX_LENGTHS.narrativeLong} />
                <Field long label="سه ویژگی مهم یک فرهنگ سازمانی خوب از نظر شما چیست؟" value={narrative.goodCultureTraits || ''} onChange={(v) => setNarrative({ goodCultureTraits: v })} maxLength={MAX_LENGTHS.narrativeMedium1500} />
                <Field long label="چه رفتاری از یک همکار برای شما قابل قبول نیست؟" value={narrative.unacceptableColleagueBehavior || ''} onChange={(v) => setNarrative({ unacceptableColleagueBehavior: v })} maxLength={MAX_LENGTHS.narrativeMedium1500} />
              </div>
            )}

            {stepIndex === 13 && (
              <div className="space-y-4">
                <ChoiceGroup
                  label="ترجیح می‌دهید با کدام فرد کار کنید؟"
                  options={['فرد A: خلاق اما گاهی غیرقابل‌اتکا', 'فرد B: منظم و قابل‌اتکا اما کم‌ایده‌تر']}
                  value={
                    narrative.preferredColleague === 'A'
                      ? 'فرد A: خلاق اما گاهی غیرقابل‌اتکا'
                      : narrative.preferredColleague === 'B'
                        ? 'فرد B: منظم و قابل‌اتکا اما کم‌ایده‌تر'
                        : ''
                  }
                  onChange={(v) => setNarrative({ preferredColleague: v.startsWith('فرد A') ? 'A' : 'B' })}
                />
                <Field long label="چرا؟" value={narrative.preferredColleagueReason || ''} onChange={(v) => setNarrative({ preferredColleagueReason: v })} maxLength={MAX_LENGTHS.narrativeShort1000} />
                <ChoiceGroup label="وقتی با یک مسئله جدید روبه‌رو می‌شوید، معمولاً اولین واکنش شما چیست؟" options={FIRST_REACTION_OPTIONS} value={narrative.firstReactionToNewProblem || ''} onChange={(v) => setNarrative({ firstReactionToNewProblem: v })} />
                <ChoiceGroup label="کدام مورد بیشتر شما را آزار می‌دهد؟" options={ANNOYANCE_OPTIONS} value={narrative.mostAnnoyingTrait || ''} onChange={(v) => setNarrative({ mostAnnoyingTrait: v })} />
                <Field long label="چرا؟" value={narrative.mostAnnoyingTraitReason || ''} onChange={(v) => setNarrative({ mostAnnoyingTraitReason: v })} maxLength={MAX_LENGTHS.narrativeShort1000} />
              </div>
            )}

            {stepIndex === 14 && (
              <div className="space-y-4">
                <ChoiceGroup label="نحوه آشنایی با ما" options={HEARD_FROM_OPTIONS} value={narrative.howHeardAboutUs || ''} onChange={(v) => setNarrative({ howHeardAboutUs: v })} />
                <Field long label="چه چیزی باعث شد برای همکاری با مجموعه ما اقدام کنید؟" value={narrative.whyApplyingToUs || ''} onChange={(v) => setNarrative({ whyApplyingToUs: v })} maxLength={MAX_LENGTHS.narrativeLong} />
                <Field long label="درباره فعالیت مجموعه ما چه می‌دانید؟" value={narrative.whatDoYouKnowAboutUs || ''} onChange={(v) => setNarrative({ whatDoYouKnowAboutUs: v })} maxLength={MAX_LENGTHS.narrativeLong} />
                <Field long label="فکر می‌کنید در این موقعیت شغلی چه ارزشی می‌توانید برای مجموعه ایجاد کنید؟" value={narrative.valueYouCanBring || ''} onChange={(v) => setNarrative({ valueYouCanBring: v })} maxLength={MAX_LENGTHS.narrativeLong} />
                <Field long label="چه نوع محیط کاری باعث می‌شود بهترین عملکردتان را داشته باشید؟" value={narrative.bestEnvironmentForYou || ''} onChange={(v) => setNarrative({ bestEnvironmentForYou: v })} maxLength={MAX_LENGTHS.narrativeMedium2000} />
                <Field long label="چه نوع محیط کاری احتمالاً باعث می‌شود عملکرد خوبی نداشته باشید؟" value={narrative.worstEnvironmentForYou || ''} onChange={(v) => setNarrative({ worstEnvironmentForYou: v })} maxLength={MAX_LENGTHS.narrativeMedium2000} />
                <Field long label="فرض کنید شش ماه از شروع همکاری گذشته و ما از استخدام شما بسیار راضی هستیم. چه اتفاق‌هایی افتاده که باعث شده بگوییم «انتخاب خوبی کردیم»؟" value={narrative.sixMonthSuccessScenario || ''} onChange={(v) => setNarrative({ sixMonthSuccessScenario: v })} maxLength={MAX_LENGTHS.narrativeLong} />
              </div>
            )}

            {/* هانی‌پات ضد اسپم — از دید کاربر واقعی مخفی است */}
            <input
              type="text"
              name="website"
              value={form.website || ''}
              onChange={(e) => setForm((f) => ({ ...f, website: e.target.value }))}
              tabIndex={-1}
              autoComplete="off"
              aria-hidden="true"
              style={{ position: 'absolute', left: '-9999px', width: 1, height: 1, opacity: 0 }}
            />
          </Panel>
        </motion.div>
      </AnimatePresence>

      {submitError ? (
        <p className="mt-3 text-[12px] font-bold text-red-500">{submitError}</p>
      ) : null}

      <div className="mt-4 flex flex-col-reverse items-stretch gap-2 sm:flex-row sm:items-center sm:justify-between sm:gap-3">
        <button
          type="button"
          onClick={goPrev}
          disabled={stepIndex === 0}
          className="rounded-2xl border px-4 py-3 text-[12.5px] font-black text-slate-600 disabled:opacity-40 sm:py-2.5"
          style={{ borderColor: HAIRLINE }}
        >
          مرحله قبل
        </button>

        {stepIndex < STEP_TITLES.length - 1 ? (
          <button
            type="button"
            onClick={goNext}
            className="rounded-2xl px-5 py-3 text-[12.5px] font-black text-white sm:py-2.5"
            style={{ backgroundColor: BRAND }}
          >
            مرحله بعد
          </button>
        ) : (
          <button
            type="button"
            onClick={handleSubmit}
            disabled={submitting}
            className="rounded-2xl px-5 py-3 text-[12.5px] font-black text-white disabled:opacity-60 sm:py-2.5"
            style={{ backgroundColor: BRAND }}
          >
            {submitting ? 'در حال ارسال...' : 'ثبت نهایی فرم'}
          </button>
        )}
      </div>
    </Shell>
  );
};

export default RecruitmentApplication;
